import React, { useState } from "react";
import { useInView } from "react-intersection-observer";

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const { ref: headerRef, inView: headerInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const { ref: formRef, inView: formInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // clear field error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.service) newErrors.service = "Please select a service";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setStatus({ type: "", text: "" });

    const formBody = new URLSearchParams(formData).toString();
    try {
      const res = await fetch("/sendmail.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody,
      });
      const data = await res.json();
      if (data.success) {
        setStatus({
          type: "success",
          text: "✓ Message sent! We’ll get back to you within 24h.",
        });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          service: "",
          message: "",
        });
        setErrors({});
      } else {
        setStatus({
          type: "error",
          text: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch (err) {
      setStatus({
        type: "error",
        text: "Network error. Please check your connection.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" style={{ background: "var(--surface)" }}>
      <div
        className={`section-tag ${headerInView ? "visible" : ""}`}
        ref={headerRef}
      >
        <span className="section-tag-icon"></span> Get In Touch
      </div>
      <h2 className={`section-title ${headerInView ? "visible" : ""}`}>
        Let's Build <em>Something</em>
        <br />
        Together
      </h2>

      <div className="contact-grid" style={{ marginTop: "2rem" }}>
        <div className={`contact-info ${formInView ? "visible" : ""}`}>
          <p style={{ color: "var(--muted)", marginBottom: "1.5rem" }}>
            Share an overview of your business and what you are looking to
            achieve. Our team will review your enquiry and respond within one
            business day with practical, tailored guidance - at no obligation.
          </p>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "1rem",
                padding: "1rem",
                background: "var(--bg)",
                borderLeft: "3px solid var(--accent)",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: "var(--accent)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--muted)",
                  }}
                >
                  Email
                </div>
                <div style={{ fontWeight: 600 }}>hello@gauweng.co.za</div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "1rem",
                padding: "1rem",
                background: "var(--bg)",
                borderLeft: "3px solid var(--blue)",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: "var(--blue)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.5 12a19.79 19.79 0 01-3.07-8.67A2 2 0 013.4 1.27h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 9a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--muted)",
                  }}
                >
                  Phone
                </div>
                <div style={{ fontWeight: 600 }}>+27 (0) 79 750 8901</div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "1rem",
                padding: "1rem",
                background: "var(--bg)",
                borderLeft: "3px solid var(--blue)",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  background: "var(--blue)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="10" r="3" />
                  <path d="M12 2a8 8 0 00-8 8c0 5.25 8 14 8 14s8-8.75 8-14a8 8 0 00-8-8z" />
                </svg>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--muted)",
                  }}
                >
                  Location
                </div>
                <div style={{ fontWeight: 600 }}>Irene, Gauteng, SA</div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`contact-form ${formInView ? "visible" : ""}`}
          ref={formRef}
        >
          <form onSubmit={handleSubmit} noValidate>
            <div className="cf-grid">
              <div className="cf-row" style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    marginBottom: "0.3rem",
                  }}
                >
                  First Name *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  style={{
                    width: "100%",
                    background: "var(--bg)",
                    border: `2px solid ${errors.firstName ? "#d32f2f" : "var(--border)"}`,
                    padding: "0.75rem",
                    fontSize: "0.9rem",
                  }}
                />
                {errors.firstName && (
                  <span className="error-message">{errors.firstName}</span>
                )}
              </div>
              <div className="cf-row" style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    marginBottom: "0.3rem",
                  }}
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Smith"
                  style={{
                    width: "100%",
                    background: "var(--bg)",
                    border: `2px solid ${errors.lastName ? "#d32f2f" : "var(--border)"}`,
                    padding: "0.75rem",
                  }}
                />
                {errors.lastName && (
                  <span className="error-message">{errors.lastName}</span>
                )}
              </div>
            </div>
            <div className="cf-row" style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  marginBottom: "0.3rem",
                }}
              >
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                style={{
                  width: "100%",
                  background: "var(--bg)",
                  border: `2px solid ${errors.email ? "#d32f2f" : "var(--border)"}`,
                  padding: "0.75rem",
                }}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>
            <div className="cf-row" style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  marginBottom: "0.3rem",
                }}
              >
                Service of Interest *
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                style={{
                  width: "100%",
                  background: "var(--bg)",
                  border: `2px solid ${errors.service ? "#d32f2f" : "var(--border)"}`,
                  padding: "0.75rem",
                }}
              >
                <option value="" disabled>
                  Select a service
                </option>
                <option>Software Development (Website/App)</option>
                <option>Search Engine Optimisation</option>
                <option>Data Analysis & BI</option>
                <option>Software Quality Assurance</option>
                <option>Bulk Notifications / SMS</option>
                <option>Multiple Services</option>
              </select>
              {errors.service && (
                <span className="error-message">{errors.service}</span>
              )}
            </div>
            <div className="cf-row" style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  marginBottom: "0.3rem",
                }}
              >
                Project Details *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please describe your project objectives, any relevant timelines, and your approximate budget - this helps us prepare a more relevant response for you."
                rows="4"
                style={{
                  width: "100%",
                  background: "var(--bg)",
                  border: `2px solid ${errors.message ? "#d32f2f" : "var(--border)"}`,
                  padding: "0.75rem",
                  resize: "vertical",
                }}
              ></textarea>
              {errors.message && (
                <span className="error-message">{errors.message}</span>
              )}
            </div>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "0.85rem",
                background: "var(--dark)",
                color: "white",
                border: "none",
                fontFamily: "inherit",
                fontSize: "0.8rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: "pointer",
                clipPath:
                  "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)",
              }}
            >
              {loading ? "Sending..." : "Request Consultation →"}
            </button>
            {status.text && (
              <div
                className={
                  status.type === "success"
                    ? "success-message"
                    : "error-message"
                }
                style={{ marginTop: "1rem", textAlign: "center" }}
              >
                {status.text}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
